import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Input,
  Label,
  Badge,
} from "reactstrap";

// PrimeReact
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

// Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// i18n
import { withTranslation } from "react-i18next";

const AdminSubscriptionPlans = props => {
  document.title = "Admin | Subscription Plans";

  // ================= STATE =================
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [editPlan, setEditPlan] = useState(null);

  const [plans, setPlans] = useState([
    {
      id: 1,
      name: "Basic",
      billingType: "Monthly",
      price: 49,
      users: 5,
      status: "Active",
    },
    {
      id: 2,
      name: "Professional",
      billingType: "Annual",
      price: 999,
      users: 20,
      status: "Active",
    },
    {
      id: 3,
      name: "Enterprise",
      billingType: "Annual",
      price: 1999,
      users: 100,
      status: "Inactive",
    },
  ]);

  const [form, setForm] = useState({
    name: "",
    billingType: "Monthly",
    price: "",
    users: "",
    status: "Active",
  });

  // ================= HANDLERS =================
  const toggleOffcanvas = () => {
    setShowOffcanvas(!showOffcanvas);
    if (!showOffcanvas) {
      setEditPlan(null);
      setForm({
        name: "",
        billingType: "Monthly",
        price: "",
        users: "",
        status: "Active",
      });
    }
  };

  const handleChange = e =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const savePlan = () => {
    if (!form.name || !form.price) return;

    if (editPlan) {
      setPlans(plans.map(p =>
        p.id === editPlan.id ? { ...p, ...form } : p
      ));
    } else {
      setPlans([
        ...plans,
        {
          id: plans.length + 1,
          ...form,
        },
      ]);
    }
    toggleOffcanvas();
  };

  const editHandler = row => {
    setEditPlan(row);
    setForm(row);
    setShowOffcanvas(true);
  };

  // ================= TEMPLATES =================
  const statusTemplate = row => (
    <Badge color={row.status === "Active" ? "success" : "secondary"}>
      {row.status}
    </Badge>
  );

  const actionTemplate = row => (
    <Button
      size="sm"
      color="link"
      onClick={() => editHandler(row)}
    >
      Edit
    </Button>
  );

  // ================= UI =================
  return (
    <div className="page-content">
      <Container fluid>

        <Breadcrumbs
          title={props.t("Admin")}
          breadcrumbItem={props.t("Subscription Plans")}
        />

        {/* ================= HEADER ================= */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0"></h4>
          <Button color="primary" onClick={toggleOffcanvas}>
            + Add Plan
          </Button>
        </div>

        {/* ================= PLANS TABLE ================= */}
        <Card>
          <CardBody>
            <DataTable
              value={plans}
              responsiveLayout="scroll"
              emptyMessage="No plans found"
            >
              <Column field="name" header="Plan Name" />
              <Column field="billingType" header="Billing Type" />
              <Column
                field="price"
                header="Price"
                body={row => `$${row.price}`}
              />
              <Column field="users" header="Users" />
              <Column
                field="status"
                header="Status"
                body={statusTemplate}
              />
              <Column
                header="Action"
                body={actionTemplate}
              />
            </DataTable>
          </CardBody>
        </Card>

        {/* ================= ADD / EDIT OFFCANVAS ================= */}
        <Offcanvas
          isOpen={showOffcanvas}
          toggle={toggleOffcanvas}
          direction="end"
        >
          <OffcanvasHeader toggle={toggleOffcanvas}>
            {editPlan ? "Edit Plan" : "Add Plan"}
          </OffcanvasHeader>

          <OffcanvasBody>
            <Row>
              <Col md="12" className="mb-3">
                <Label>Plan Name</Label>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                />
              </Col>

              <Col md="12" className="mb-3">
                <Label>Billing Type</Label>
                <Input
                  type="select"
                  name="billingType"
                  value={form.billingType}
                  onChange={handleChange}
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Annual">Annual</option>
                </Input>
              </Col>

              <Col md="12" className="mb-3">
                <Label>Price</Label>
                <Input
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                />
              </Col>

              <Col md="12" className="mb-3">
                <Label>Allowed Users</Label>
                <Input
                  type="number"
                  name="users"
                  value={form.users}
                  onChange={handleChange}
                />
              </Col>

              <Col md="12" className="mb-4">
                <Label>Status</Label>
                <Input
                  type="select"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Input>
              </Col>

              <Col md="12" className="text-end">
                <Button
                  color="secondary"
                  className="me-2"
                  onClick={toggleOffcanvas}
                >
                  Cancel
                </Button>
                <Button color="primary" onClick={savePlan}>
                  Save
                </Button>
              </Col>
            </Row>
          </OffcanvasBody>
        </Offcanvas>

      </Container>
    </div>
  );
};

AdminSubscriptionPlans.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(AdminSubscriptionPlans);
